import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { loginValidationSchema } from "../../../lib/validation/loginValidation";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { loginUser, resetAuth } from "../../../slices/authSlice";
import { toast } from "react-toastify";
import Slideshow from "../../../components/Slideshow";
import { slides } from "../../../assets/slideShowData";
import { logo } from "../../../assets/exports";
import { Eye, EyeClosedIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((s) => s.auth);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      identifier: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(loginUser(values)).unwrap();
        toast.success("Welcome back!");        
        navigate("/");
      } catch (err: any) {
        toast.error(err || "Login failed");
      }
    },
  });

  // console.log("auth status", auth.status);

  useEffect(() => {
    dispatch(resetAuth())
  }, []);

  return (
    <div className="min-h-screen lg:flex-row lg:gap-4 lg:justify-between text-left flex items-center justify-center p-1">
      <div className="hidden lg:block lg:w-1/2">
        <Slideshow slides={slides} />
      </div>

       {/* RIGHT - LOGIN FORM */}
      <div className="flex w-full lg:w-1/2 items-center justify-center lg:p-4">
        <div className="w-full bg-white rounded-2xl md:shadow-md p-6 mx-auto sm:max-w-sm md:max-w-md lg:max-w-md">

          {/* RESPONSIVE HEADER WITH IMAGE */}
          <div className="flex flex-col items-center my-5 px-2">
            <img
              src={logo}
              className="lg:w-50 lg:h-50 w-60 h-60 object-contain cursor-pointer"
              alt="StorePilot Logo"
              onClick={() => navigate("/")}
            />
            <div className="text-center w-full">
              <p className="text-xl text-gray-500">Sign in to your account</p>
            </div>
          </div>

          {/* FORMIK FORM GOES HERE (unchanged) */}
          <form onSubmit={formik.handleSubmit} className="space-y-4">
          {/* IDENTIFIER */}
          <div>
            <label htmlFor="identifier" className="form-label">
              Email or Username
            </label>
            <input
              id="identifier"
              name="identifier"
              type="text"
              value={formik.values.identifier}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`form-input
                ${formik.touched.identifier && formik.errors.identifier ? "border-red-500" : "border-gray-200"}`}
              placeholder="you@example.com or username"
            />
            {formik.touched.identifier && formik.errors.identifier && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.identifier}</p>
            )}
          </div>

          {/* PASSWORD */}
          <div>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-input
                  ${formik.touched.password && formik.errors.password ? "border-red-500" : "border-gray-200"}`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500"
              >
                {showPassword ? <EyeClosedIcon /> : <Eye />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="text-xs text-red-500 mt-1">{formik.errors.password}</p>
            )}
          </div>

          {/* SUBMIT */}
          <div>
            <button
              type="submit"
              className="form-btn"
              disabled={auth.status === "loading"}
            >
               {auth.status === "loading" ? (
                  <div className="flex items-center gap-2">
                    <span className="loader"></span>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  "Sign in"
                )}
            </button>
          </div>
        </form>

        <footer className="mt-6 text-left text-sm text-gray-500">
          <p>
            Don’t have an account?{" "}
            <a href="/register" className="text-primary hover:underline">
              Create one
            </a>
          </p>
          <p>
            <a href="/forgot-password" className="text-primary hover:underline">
              Forgot your password?
            </a>
          </p>
        </footer>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
